import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {Spinner} from '../../Spinner';
import {useUserModules} from '../../../context/UserModulesContext';
import {AccessService} from '../../../utils/AccessService';
import {ContextPackRequestModal} from './ContextPackRequestModal';
import {useContextPack} from './useContextPack';
import {
    kindLabelKey, kindDescKey, kindIcon, ctaPathForItem,
} from './contextPackConstants';

// "Paquete de Contexto del Cliente" (A1-FE): checklist curado de qué material sumar para que la
// IA conozca al cliente. Reusa el lenguaje visual del IAContextMeter (filas -ok con tilde verde /
// -missing con círculo hueco). No inventa: prioriza los CORE, colapsa el material secundario y el
// "qué no hace falta subir", y ofrece pedirle el material al cliente (modal A4).
//
// props:
//   clientId  — id del AgencyClient.
//   mode      — 'full' (checklist completo, empty state del Resumen) | 'compact' (strip de 1 línea
//               "Contexto del cliente: {n}/{m}" que expande la lista inline; se auto-oculta si está
//               completo). Default: 'full'.
//   clientName — nombre del cliente para el mensaje del modal "Pedir material" (lo pasa el padre,
//                que ya lo tiene resuelto — evita un fetch duplicado y frágil).
//   refreshToken — contador que sube cuando entró material, se re-etiquetó o se borró un documento.
export function MemoryContextPack({clientId, clientName = '', mode = 'full', refreshToken = 0}) {
    const {t} = useTranslation();
    const [showSecondary, setShowSecondary] = useState(false);
    const [showDoNotUpload, setShowDoNotUpload] = useState(false);
    const [expanded, setExpanded] = useState(false); // solo modo compacto
    const [requestOpen, setRequestOpen] = useState(false);

    // El ítem del media plan sólo tiene sentido si la agencia puede usarlo. Mismo predicado que el
    // tab y su route guard (`ClientMediaPlanTab`): si no, el CTA lleva a una pantalla que redirige
    // a Monitoreo. Mientras los módulos no cargaron va `undefined` = "no sé todavía" y no se filtra
    // nada, para no sacar y volver a poner el ítem delante del usuario.
    const {modules, mediaPlanEnabled, loaded} = useUserModules();
    const mediaPlanAvailable = !loaded
        ? undefined
        : (mediaPlanEnabled === true && (modules || []).includes(AccessService.MODULE_DASHBO_MCP));

    const {loading, failed, summary, refetch} = useContextPack(
        clientId, {refreshToken, mediaPlanAvailable});

    if (loading) {
        return <div className="box-loader"><Spinner color="positive small"/></div>;
    }

    if (failed) {
        // En `compact` el checklist es un adorno arriba de las fuentes: si falla, colapsa en
        // silencio (mismo criterio que el IAContextMeter). En `full` ES la pantalla —el empty state
        // del Resumen—, así que devolver null dejaba un bloque en blanco sin explicación.
        if (mode === 'compact') return null;
        return (
            <div className="cmem-cpack -full -failed">
                <p className="cmem-cpack-subtitle">{t('client_memory.context_pack.load_error')}</p>
                <button type="button" className="btn -alt -negative cmem-cpack-request-btn"
                        onClick={refetch}>
                    {t('client_memory.context_pack.retry')}
                </button>
            </div>
        );
    }

    const {core, secondary, doNotUpload, corePresent, coreTotal, requestableMissing,
        isComplete} = summary;

    // Modo compacto y ya completo → no renderizar nada (no molestar cuando no hay nada que hacer).
    // Sin CORE tampoco hay nada que mostrar: un strip "0/0" con la lista vacía no es información.
    // (`isComplete` exige `coreTotal > 0`, así que este caso no cae en la condición de arriba.)
    if (mode === 'compact' && (isComplete || coreTotal === 0)) return null;

    // Fila de un ítem: chip con el ícono del tipo de documento (escaneabilidad) + label + desc.
    // Presente → chip verde con tilde al final; falta → chip neutro + link con chevron a la acción.
    // El estado va TAMBIÉN como texto (`sr-only`): con sólo color + ícono, un lector de pantalla lee
    // exactamente lo mismo para "ya lo tenés" y "falta".
    const renderRow = (item, {withCta = true} = {}) => {
        const ctaPath = withCta && !item.present ? ctaPathForItem(item, clientId) : null;
        const label = t(kindLabelKey(item.kind));
        const stateKey = item.present
            ? 'client_memory.context_pack.state_present'
            : 'client_memory.context_pack.state_missing';
        return (
            <li key={item.kind} className={`cmem-cpack-item ${item.present ? '-ok' : '-missing'}`}>
                <span className="cmem-cpack-item-icon">
                    <i className={`fas fa-${kindIcon(item.kind)}`} aria-hidden="true"/>
                </span>
                <div className="cmem-cpack-item-text">
                    {ctaPath
                        ? (
                            <Link className="cmem-cpack-item-label" to={ctaPath}>
                                {label}
                                <span className="sr-only"> — {t(stateKey)}</span>
                                <i className="fas fa-chevron-right cmem-cpack-item-go" aria-hidden="true"/>
                            </Link>
                        )
                        : (
                            <span className="cmem-cpack-item-label">
                                {label}
                                <span className="sr-only"> — {t(stateKey)}</span>
                                {item.present
                                    && <i className="fas fa-check cmem-cpack-item-check" aria-hidden="true"/>}
                            </span>
                        )}
                    <span className="cmem-cpack-item-desc">{t(kindDescKey(item.kind))}</span>
                </div>
            </li>
        );
    };

    const renderCoreList = () => (
        <ul className="cmem-cpack-list">
            {core.map((item) => renderRow(item))}
        </ul>
    );

    // Modo compacto: strip de 1 línea + chevron que expande el checklist completo inline.
    if (mode === 'compact') {
        return (
            <div className={`cmem-cpack -compact ${expanded ? '-expanded' : ''}`}>
                <button
                    type="button"
                    className="cmem-cpack-strip"
                    aria-expanded={expanded}
                    aria-controls="cmem-cpack-strip-panel"
                    onClick={() => setExpanded((v) => !v)}
                >
                    <span className="cmem-cpack-strip-label">
                        {t('client_memory.context_pack.progress', {present: corePresent, total: coreTotal})}
                    </span>
                    <span className="cmem-cpack-strip-hint">{t('client_memory.context_pack.compact_hint')}</span>
                    <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} cmem-cpack-chevron`}
                       aria-hidden="true"/>
                </button>
                {expanded && (
                    <div className="cmem-cpack-expanded" id="cmem-cpack-strip-panel">
                        {renderCoreList()}
                        {requestableMissing.length > 0 && (
                            <button type="button" className="btn -alt -negative cmem-cpack-request-btn"
                                    onClick={() => setRequestOpen(true)}>
                                <i className="fas fa-envelope" aria-hidden="true"/>
                                {' '}{t('client_memory.context_pack.request_button')}
                            </button>
                        )}
                    </div>
                )}
                <ContextPackRequestModal
                    isOpen={requestOpen}
                    onClose={() => setRequestOpen(false)}
                    clientName={clientName}
                    missingItems={requestableMissing}
                />
            </div>
        );
    }

    // Modo full: título + subtítulo + CORE como filas + secciones colapsables.
    return (
        <div className="cmem-cpack -full">
            <div className="cmem-cpack-header">
                <h3 className="cmem-cpack-title">{t('client_memory.context_pack.title')}</h3>
                <span className="cmem-cpack-progress">
                    {t('client_memory.context_pack.progress', {present: corePresent, total: coreTotal})}
                </span>
            </div>
            {/* Un segmento por ítem, pintado por SU estado. Pintarlos por posición (`i < present`)
                hacía que con el 4º ítem presente se marcaran los 3 primeros — la barra contradecía
                a la lista de abajo. */}
            <div className="cmem-cpack-segments" role="presentation" aria-hidden="true">
                {core.map((it) => (
                    <span key={it.kind} className={it.present ? '-done' : ''}/>
                ))}
            </div>
            <p className="cmem-cpack-subtitle">{t('client_memory.context_pack.subtitle')}</p>

            {renderCoreList()}

            {requestableMissing.length > 0 && (
                <div className="cmem-cpack-actions">
                    <button type="button" className="btn -alt -negative cmem-cpack-request-btn"
                            onClick={() => setRequestOpen(true)}>
                        <i className="fas fa-envelope" aria-hidden="true"/>
                        {' '}{t('client_memory.context_pack.request_button')}
                    </button>
                </div>
            )}

            {/* Catch-all (feedback Pablo): el "espacio abierto" para material que no encaja en la
                lista curada — "subí lo que tengas". Saca el techo de casilleros cerrados del
                checklist. Rutea al mismo flujo agnóstico de Drive que los ítems MEMORY; lo que no
                matchea ningún tipo lo clasifica el backend como document_kind OTHER (ya soportado). */}
            <Link className="cmem-cpack-catchall" to={`/client/${clientId}/memory/sources?connect=drive`}>
                <span className="cmem-cpack-catchall-icon">
                    <i className="fas fa-file-upload" aria-hidden="true"/>
                </span>
                <div className="cmem-cpack-catchall-text">
                    <span className="cmem-cpack-catchall-title">{t('client_memory.context_pack.catchall.title')}</span>
                    <span className="cmem-cpack-catchall-desc">{t('client_memory.context_pack.catchall.desc')}</span>
                </div>
                <i className="fas fa-chevron-right cmem-cpack-catchall-go" aria-hidden="true"/>
            </Link>

            {secondary.length > 0 && (
                <div className="cmem-cpack-collapsible">
                    <button type="button" className="cmem-cpack-toggle" aria-expanded={showSecondary}
                            aria-controls="cmem-cpack-secondary"
                            onClick={() => setShowSecondary((v) => !v)}>
                        <i className={`fas fa-chevron-${showSecondary ? 'up' : 'down'}`} aria-hidden="true"/>
                        {t('client_memory.context_pack.secondary_toggle')} ({secondary.length})
                    </button>
                    {showSecondary && (
                        <ul className="cmem-cpack-list -secondary" id="cmem-cpack-secondary">
                            {secondary.map((item) => renderRow(item))}
                        </ul>
                    )}
                </div>
            )}

            {doNotUpload.length > 0 && (
                <div className="cmem-cpack-collapsible">
                    <button type="button" className="cmem-cpack-toggle" aria-expanded={showDoNotUpload}
                            aria-controls="cmem-cpack-dont"
                            onClick={() => setShowDoNotUpload((v) => !v)}>
                        <i className={`fas fa-chevron-${showDoNotUpload ? 'up' : 'down'}`} aria-hidden="true"/>
                        {t('client_memory.context_pack.do_not_upload_toggle')} ({doNotUpload.length})
                    </button>
                    {showDoNotUpload && (
                        <ul className="cmem-cpack-list -dont" id="cmem-cpack-dont">
                            {doNotUpload.map((item) => (
                                <li key={item.kind} className="cmem-cpack-item -dont">
                                    <span className="cmem-cpack-item-icon">
                                        <i className="fas fa-ban" aria-hidden="true"/>
                                    </span>
                                    <div className="cmem-cpack-item-text">
                                        <span className="cmem-cpack-item-label">{t(kindLabelKey(item.kind))}</span>
                                        <span className="cmem-cpack-item-desc">{t(kindDescKey(item.kind))}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <ContextPackRequestModal
                isOpen={requestOpen}
                onClose={() => setRequestOpen(false)}
                clientName={clientName}
                missingItems={requestableMissing}
            />
        </div>
    );
}
